import { defineComponent as _defineComponent } from 'vue'

export enum OzInputSizePreset {
  H32px = 'H32px',
  H40px = 'H40px',
}

export enum OzInputColorScheme {
  Translucent = 'Translucent',
  Secondary = 'Secondary',
}

const __default__ = {
  inheritAttrs: false,
}

import { uniqueId } from 'lodash-es'
import { computed, ref, useSlots, withDefaults } from 'vue'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'OzInput',
  props: {
    type: { default: 'text' },
    darkMode: { type: [Boolean, String], default: 'auto' },
    colorScheme: { default: OzInputColorScheme.Translucent },
    valid: { default: 'unknown' },
    disabled: { type: Boolean, default: false },
    testId: { default: '' },
    validationMessage: { default: '' },
    textAlign: { default: undefined },
    sizePreset: { default: OzInputSizePreset.H40px },
    xBorder: { type: Boolean, default: true },
    shouldTruncateText: { type: Boolean, default: false },
    value: { default: undefined },
    modelValue: { default: undefined }
  },
  emits: ["focusin", "focusout", "blur", "input", "update:modelValue"],
  setup(__props: any, { expose, emit }: { emit: ({
  (event: 'focusin', value: FocusEvent): void
  (event: 'focusout', value: FocusEvent): void
  (event: 'blur', value: FocusEvent): void
  (event: 'input', value: InputEvent): void // vue 2 v-model, refer to https://v3-migration.vuejs.org/breaking-changes/v-model.html
  (event: 'update:modelValue', value: string): void // vue 3 v-model, refer to https://vuejs.org/guide/components/v-model.html
}), expose: any, slots: any, attrs: any }) {

const props = __props as { type: string, darkMode: true | false | 'auto', colorScheme: OzInputColorScheme, valid: 'yes' | 'no' | 'unknown', disabled: boolean, testId: string, validationMessage: string, textAlign: 'start' | 'end' | 'center', sizePreset: OzInputSizePreset, xBorder: boolean, shouldTruncateText: boolean, value: string, modelValue: string }



const isFocused = ref(false)

const internalValue = computed({
  get: () => props.modelValue || props.value,
  set: (val) => {
    emit('update:modelValue', val)
  },
})

const inputElement = ref<HTMLInputElement | null>(null)
const validationMessageId = uniqueId('validationMessageId')
const isValid = computed(() => props.valid !== 'no')
const xValidationMessage = computed(() => !isValid.value && !!props.validationMessage?.length)

const slots = useSlots()
const hasExtraContent = computed(() => slots.extraContent)

const backgroundClasses = computed(() => {
  if (props.colorScheme === OzInputColorScheme.Secondary) {
    if (isFocused.value) {
      return {
        'bg-light-ui-100': props.darkMode === false,
        'bg-dark-ui-100': props.darkMode === true,
        'bg-light-ui-100 dark:bg-dark-ui-100': props.darkMode === 'auto',
      }
    } else {
      return {
        'bg-grey-100': props.darkMode === false,
        'bg-grey-850': props.darkMode === true,
        'bg-grey-100 dark:bg-grey-850': props.darkMode === 'auto',
      }
    }
  }

  return ''
})

const borderClasses = computed(() => {
  if (!props.xBorder) return ''

  if (props.colorScheme === OzInputColorScheme.Translucent) {
    const commonClasses = {
      'border-1': props.sizePreset === OzInputSizePreset.H32px,
      'border-2': props.sizePreset === OzInputSizePreset.H40px,
      'border-solid': true,
    }

    if (!isValid.value) return { ...commonClasses, 'border-danger-100': true }

    if (props.disabled)
      return {
        ...commonClasses,
        'border-grey-300': props.darkMode === false,
        'border-white-disabled': props.darkMode === true,
        'border-grey-300 dark:border-white-disabled': props.darkMode === 'auto',
      }

    if (isFocused.value)
      return {
        ...commonClasses,
        'border-grape-500': props.darkMode === false,
        'border-canary-500': props.darkMode === true,
        'border-grape-500 dark:border-canary-500': props.darkMode === 'auto',
      }

    return {
      ...commonClasses,
      'border-dark-ui-100': props.darkMode === false,
      'border-light-ui-100': props.darkMode === true,
      'border-dark-ui-100 dark:border-light-ui-100': props.darkMode === 'auto',
    }
  } else if (props.colorScheme === OzInputColorScheme.Secondary) {
    const commonClasses = {
      'border-1': props.sizePreset === OzInputSizePreset.H32px,
      'border-2': props.sizePreset === OzInputSizePreset.H40px,
      'border-solid': true,
    }

    if (!isValid.value) return { ...commonClasses, 'border-danger-100': true }

    if (isFocused.value)
      return {
        ...commonClasses,
        'border-grape-500': props.darkMode === false,
        'border-canary-500': props.darkMode === true,
        'border-grape-500 dark:border-canary-500': props.darkMode === 'auto',
      }

    return {
      ...commonClasses,
      'border-transparent': true,
    }
  } else {
    return ''
  }
})



// in vue-2, v-model is binded to the value prop and an inputEvent is expected to be emitted on input.
const onInput = (event: Event) => {
  emit('input', event as InputEvent)
}

const focusin = (event: FocusEvent) => {
  isFocused.value = true
  // Emit the focusin event again after we are done with our internal handling
  // So the user of the component can listen to it if they want
  emit('focusin', event)
}

const focusout = (event: FocusEvent) => {
  isFocused.value = false
  // Emit the focusout event again after we are done with our internal handling
  // So the user of the component can listen to it if they want
  emit('focusout', event)
}

const blur = (event: FocusEvent) => {
  // Emit the blur event again after we are done with our internal handling
  // So the user of the component can listen to it if they want
  emit('blur', event)
}

const focusInput = () => {
  inputElement.value?.focus()
}

const selectInput = () => {
  inputElement.value?.select()
}

const blurInput = () => {
  inputElement.value?.blur()
}

expose({
  focusInput,
  selectInput,
  blurInput,
})

return { __sfc: true,OzInputSizePreset, OzInputColorScheme, props, isFocused, internalValue, inputElement, validationMessageId, isValid, xValidationMessage, slots, hasExtraContent, backgroundClasses, borderClasses, emit, onInput, focusin, focusout, blur, focusInput, selectInput, blurInput }
}

})